import React, { useEffect, useState } from "react";
import { X, Eye, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import api, { eur, imgUrl } from "../../lib/api";

const STATUSES = ["Νέα", "Σε επεξεργασία", "Απεστάλη", "Ολοκληρώθηκε", "Ακυρώθηκε"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const updateStatus = async (order, status) => {
    try {
      const { data } = await api.put(`/admin/orders/${order.id}`, { status });
      setOrders((prev) => prev.map((o) => o.id === order.id ? data : o));
      if (selected?.id === order.id) setSelected(data);
      toast.success("Η κατάσταση ενημερώθηκε");
    } catch { toast.error("Σφάλμα"); }
  };

  const payLabel = (o) => o.payment_method === "cod"
    ? "Αντικαταβολή"
    : (o.payment_status === "paid" ? "Κάρτα — Πληρωμένη" : "Κάρτα — Εκκρεμεί");

  return (
    <div>
      <h1 className="font-serif-display text-3xl text-[#1E3A2F] mb-6">Παραγγελίες</h1>

      {orders.length ? (
        <div className="bg-white border border-[#E5E9E4] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#536D61] border-b border-[#E5E9E4]">
                <th className="py-3 px-4 font-medium">Ημ/νία</th>
                <th className="py-3 px-4 font-medium">Πελάτης</th>
                <th className="py-3 px-4 font-medium">Πληρωμή</th>
                <th className="py-3 px-4 font-medium">Κατάσταση</th>
                <th className="py-3 px-4 font-medium text-right">Σύνολο</th>
                <th className="py-3 px-4 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#F0F2EE] hover:bg-[#F9F8F6]" data-testid={`order-row-${o.id}`}>
                  <td className="py-3 px-4 text-[#536D61] whitespace-nowrap">{new Date(o.created_at).toLocaleDateString("el-GR")}</td>
                  <td className="py-3 px-4">
                    <p className="font-medium text-[#1E3A2F]">{o.customer?.name}</p>
                    <p className="text-xs text-[#536D61]">{o.customer?.phone}</p>
                  </td>
                  <td className="py-3 px-4 text-[#536D61]">{payLabel(o)}</td>
                  <td className="py-3 px-4">
                    <select value={o.status} onChange={(e) => updateStatus(o, e.target.value)} data-testid={`order-status-${o.id}`} className="text-xs border border-[#E5E9E4] rounded-sm px-2 py-1.5 bg-white focus:border-[#1E3A2F] outline-none">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="py-3 px-4 text-right font-medium">{eur(o.total)}</td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => setSelected(o)} data-testid={`view-order-${o.id}`} className="p-2 text-[#536D61] hover:text-[#1E3A2F]"><Eye className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-dashed border-[#E5E9E4] rounded-sm py-16 text-center text-[#536D61]">
          <ShoppingCart className="h-10 w-10 text-[#1E3A2F]/30 mx-auto mb-3" />
          Δεν υπάρχουν παραγγελίες ακόμη.
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto bg-black/50">
          <div className="bg-white rounded-sm w-full max-w-lg my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E9E4]">
              <h2 className="font-serif-display text-2xl text-[#1E3A2F]">Λεπτομέρειες παραγγελίας</h2>
              <button onClick={() => setSelected(null)}><X className="h-5 w-5 text-[#536D61]" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="text-xs text-[#536D61] font-mono">#{selected.id}</div>
              <div>
                <h3 className="font-semibold text-[#1E3A2F] mb-2 text-sm">Στοιχεία πελάτη</h3>
                <div className="text-sm text-[#2a3a33] space-y-0.5">
                  <p>{selected.customer?.name} — {selected.customer?.phone}</p>
                  <p>{selected.customer?.email}</p>
                  <p>{selected.customer?.address}, {selected.customer?.city} {selected.customer?.postal_code}</p>
                  {selected.customer?.notes && <p className="italic text-[#536D61]">«{selected.customer.notes}»</p>}
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-[#1E3A2F] mb-2 text-sm">Προϊόντα</h3>
                <div className="space-y-2">
                  {selected.items?.map((it, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-sm overflow-hidden bg-[#E8F0E1] shrink-0">{it.image && <img src={imgUrl(it.image)} alt="" className="h-full w-full object-cover" />}</div>
                      <div className="flex-1 text-sm"><span className="text-[#1E3A2F]">{it.name}</span> <span className="text-[#536D61]">× {it.quantity}</span></div>
                      <div className="text-sm font-medium">{eur(it.price * it.quantity)}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t border-[#E5E9E4] pt-3 space-y-1 text-sm">
                <div className="flex justify-between text-[#536D61]"><span>Υποσύνολο</span><span>{eur(selected.subtotal)}</span></div>
                <div className="flex justify-between text-[#536D61]"><span>Μεταφορικά</span><span>{selected.shipping ? eur(selected.shipping) : "Δωρεάν"}</span></div>
                <div className="flex justify-between font-semibold text-[#1E3A2F] text-base pt-1"><span>Σύνολο</span><span>{eur(selected.total)}</span></div>
                <div className="flex justify-between text-[#536D61] pt-1"><span>Τρόπος πληρωμής</span><span>{payLabel(selected)}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
